import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Fragment, useState, type ReactElement } from "react";
import {
  Field,
  FormPage,
  FormSection,
  MoneyInput,
  QtyInput,
  QueryStatus,
  ScaledInput,
  useToast,
} from "@cx/ui";
import type { PurchaseOrderCalc, PurchaseOrderFormOptions, PurchaseOrderView } from "@cx/contracts";
import {
  FREIGHT_MODES,
  FREIGHT_MODE_LABELS,
  formatBRL,
  formatDateBR,
  formatPct,
  todayInSaoPaulo,
  toPct4,
  type FreightMode,
} from "@cx/domain";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { ContactPicker } from "../finance/ContactPicker";
import { ProductSearch } from "../prices/ProductSearch";
import {
  emptyPurchaseForm,
  itemError,
  purchaseBody,
  purchaseFormFromView,
  purchasePreviewBody,
  rowKey,
  type PurchaseItemRow,
  type PurchaseOrderForm,
} from "./labels";

const pctNumber = (pct4: number) => formatPct(pct4).replace("%", "");

/** Inclusão (/suprimentos/pedidos-de-compra/novo) e edição (…/$id/editar) do pedido de compra. */
export function PurchaseOrderFormPage(): ReactElement {
  const params = useParams({ strict: false }) as { id?: string };
  const id = params.id;
  const { companyId, api } = useSession();
  const options = useQuery({
    queryKey: ["purchase-orders", companyId, "form-options"],
    queryFn: () => api<PurchaseOrderFormOptions>("/purchase-orders/form-options"),
  });
  const order = useQuery({
    queryKey: ["purchase-orders", companyId, "view", id],
    enabled: !!id,
    refetchOnMount: "always",
    queryFn: () => api<PurchaseOrderView>(`/purchase-orders/${id}`),
  });
  if (!options.data || (id && !order.data))
    return (
      <QueryStatus loading={options.isLoading || order.isLoading} error={options.error ?? order.error} />
    );
  if (order.data?.lockedReason)
    return (
      <div className="alert alert-warning" role="alert">
        {order.data.lockedReason}
      </div>
    );
  return <PurchaseFormBody key={id ?? "new"} options={options.data} order={order.data ?? null} />;
}

function PurchaseFormBody({
  options,
  order,
}: {
  options: PurchaseOrderFormOptions;
  order: PurchaseOrderView | null;
}) {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [f, setF] = useState<PurchaseOrderForm>(() =>
    order ? purchaseFormFromView(order) : emptyPurchaseForm(todayInSaoPaulo()),
  );
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Uma chave por tela de inclusão: clique duplo ou repetição da rede não criam dois pedidos.
  const [idemKey] = useState(() => crypto.randomUUID());
  const set = <K extends keyof PurchaseOrderForm>(k: K, v: PurchaseOrderForm[K]) => {
    setF((x) => ({ ...x, [k]: v }));
    setDirty(true);
  };
  const setItem = (key: string, patch: Partial<PurchaseItemRow>) => {
    setF((x) => ({ ...x, items: x.items.map((i) => (i.key === key ? { ...i, ...patch } : i)) }));
    setDirty(true);
  };

  const pb = purchasePreviewBody(f);
  const preview = useQuery({
    queryKey: ["purchase-orders", companyId, "preview", pb],
    enabled: !!pb,
    retry: false,
    placeholderData: keepPreviousData,
    queryFn: () =>
      api<PurchaseOrderCalc>("/purchase-orders/preview", { method: "POST", body: JSON.stringify(pb) }),
  });
  const calc = pb ? preview.data : undefined;

  const save = useMutation({
    mutationFn: async () => {
      const body = JSON.stringify(purchaseBody(f));
      if (order) {
        await api(`/purchase-orders/${order.id}`, { method: "PUT", body });
        return order.id;
      }
      const r = await api<{ id: string }>("/purchase-orders", {
        method: "POST",
        body,
        headers: { "Idempotency-Key": idemKey },
      });
      return r.id;
    },
    onSuccess: (savedId) => {
      toast("success", "Pedido de compra salvo");
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["purchase-orders", companyId] });
      navigate({ to: "/suprimentos/pedidos-de-compra/$id", params: { id: savedId } });
    },
    onError: (e) => {
      setErrors(e instanceof ApiFailure && e.fields ? e.fields : {});
      if (e instanceof ApiFailure && e.code === "invalid_contact") setErrors({ supplierId: e.message });
      toast("danger", errorMessage(e));
    },
  });

  const title = order ? `Pedido de compra nº ${order.number}` : "Novo pedido de compra";
  const back = () =>
    order
      ? navigate({ to: "/suprimentos/pedidos-de-compra/$id", params: { id: order.id } })
      : navigate({ to: "/suprimentos/pedidos-de-compra" });
  const previewError = pb && preview.error ? errorMessage(preview.error) : null;

  return (
    <FormPage
      title={title}
      breadcrumb={["Suprimentos", "Pedidos de compra", order ? String(order.number) : "Novo"]}
      onSave={() => save.mutate()}
      onCancel={back}
      saving={save.isPending}
      dirty={dirty}
      aside={
        <div className="position-sticky" style={{ top: "1rem" }}>
          <TotalsBox calc={calc} error={previewError} loading={preview.isFetching} />
        </div>
      }
    >
      <FormSection title="Fornecedor e pedido">
        <div className="row">
          <div className="col-md-8">
            <ContactPicker
              label="Fornecedor"
              value={f.supplier}
              error={errors.supplierId}
              onChange={(c) => set("supplier", c)}
            />
          </div>
          <Field
            label="Ordem de compra"
            className="col-md-4"
            error={errors.purchaseRef}
            hint="Referência do seu controle."
          >
            <input
              value={f.purchaseRef}
              maxLength={60}
              onChange={(e) => set("purchaseRef", e.target.value)}
            />
          </Field>
          <Field label="Data" required className="col-md-3" error={errors.date}>
            <input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} />
          </Field>
          <Field label="Data prevista" className="col-md-3" error={errors.expectedDate}>
            <input type="date" value={f.expectedDate} onChange={(e) => set("expectedDate", e.target.value)} />
          </Field>
          <Field
            label="Depósito de entrada"
            className="col-md-6"
            error={errors.warehouseId}
            hint="Sugerido no recebimento."
          >
            <select
              className="form-select"
              value={f.warehouseId}
              onChange={(e) => set("warehouseId", e.target.value)}
            >
              <option value="">Padrão da empresa</option>
              {order?.warehouse && !options.warehouses.some((w) => w.id === order.warehouse!.id) && (
                <option value={order.warehouse.id}>{order.warehouse.name}</option>
              )}
              {options.warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </FormSection>

      <FormSection title="Itens">
        {errors.items && <div className="alert alert-danger py-2">{errors.items}</div>}
        <ItemsTable
          f={f}
          calc={calc}
          errors={errors}
          setItem={setItem}
          onRemove={(key) =>
            set(
              "items",
              f.items.filter((i) => i.key !== key),
            )
          }
        />
        <ProductSearch
          id="po-product"
          label="Adicionar produto"
          action="Adicionar"
          onPick={(p) =>
            set("items", [
              ...f.items,
              {
                key: rowKey(),
                productId: p.id,
                description: p.name,
                code: p.sku,
                unit: p.unit ?? "",
                qtyQ4: 10_000,
                unitPriceCents: null,
                ipiPct4: null,
              },
            ])
          }
        />
      </FormSection>

      <FormSection title="Descontos, frete, impostos e pagamento">
        <div className="row">
          <fieldset className="col-md-6 mb-3">
            <legend className="form-label fs-6">Desconto geral</legend>
            {(
              [
                ["value", "Em valor"],
                ["pct", "Em percentual"],
              ] as const
            ).map(([m, label]) => (
              <div key={m} className="form-check form-check-inline">
                <input
                  id={`po-discount-${m}`}
                  type="radio"
                  name="po-discount-mode"
                  className="form-check-input"
                  checked={f.discountMode === m}
                  onChange={() => set("discountMode", m)}
                />
                <label className="form-check-label" htmlFor={`po-discount-${m}`}>
                  {label}
                </label>
              </div>
            ))}
          </fieldset>
          {f.discountMode === "value" ? (
            <Field label="Desconto" className="col-md-6" error={errors.discountCents}>
              <MoneyInput value={f.discountCents} onChange={(v) => set("discountCents", v)} />
            </Field>
          ) : (
            <Field label="Desconto (%)" className="col-md-6" error={errors.discountPct4}>
              <ScaledInput
                value={f.discountPct4}
                parse={toPct4}
                format={pctNumber}
                suffix="%"
                onChange={(v) => set("discountPct4", v)}
              />
            </Field>
          )}
          <Field label="Frete" className="col-md-4" error={errors.freightCents}>
            <MoneyInput value={f.freightCents} onChange={(v) => set("freightCents", v)} />
          </Field>
          <Field label="Outras despesas" className="col-md-4" error={errors.otherExpensesCents}>
            <MoneyInput value={f.otherExpensesCents} onChange={(v) => set("otherExpensesCents", v)} />
          </Field>
          <Field label="ICMS-ST" className="col-md-4" error={errors.icmsStCents}>
            <MoneyInput value={f.icmsStCents} onChange={(v) => set("icmsStCents", v)} />
          </Field>
          <Field
            label="Condição de pagamento"
            className="col-md-6"
            error={errors.paymentTermId}
            hint="As contas a pagar de cada recebimento seguem esta condição."
          >
            <select
              className="form-select"
              value={f.paymentTermId}
              onChange={(e) => set("paymentTermId", e.target.value)}
            >
              <option value="">À vista</option>
              {order?.paymentTerm && !options.paymentTerms.some((t) => t.id === order.paymentTerm!.id) && (
                <option value={order.paymentTerm.id}>{order.paymentTerm.name}</option>
              )}
              {options.paymentTerms.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Categoria das contas" className="col-md-6" error={errors.financialCategoryId}>
            <select
              className="form-select"
              value={f.financialCategoryId}
              onChange={(e) => set("financialCategoryId", e.target.value)}
            >
              <option value="">Compras de mercadorias</option>
              {order?.financialCategory &&
                !options.categories.some((c) => c.id === order.financialCategory!.id) && (
                  <option value={order.financialCategory.id}>{order.financialCategory.name}</option>
                )}
              {options.categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        {calc && calc.installments.length > 0 && (
          <table className="table table-sm w-auto" aria-label="Parcelas previstas">
            <thead>
              <tr>
                <th scope="col">Parcela</th>
                <th scope="col">Vencimento previsto</th>
                <th scope="col" className="text-end">
                  Valor
                </th>
              </tr>
            </thead>
            <tbody>
              {calc.installments.map((p) => (
                <tr key={p.number}>
                  <td>{p.number}</td>
                  <td>{formatDateBR(p.dueDate)}</td>
                  <td className="text-end">{formatBRL(p.amountCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </FormSection>

      <FormSection
        title="Transporte e observações"
        defaultOpen={!!(order?.carrier || order?.notes || order?.internalNotes)}
      >
        <div className="row">
          <Field label="Transportador" className="col-md-6" error={errors.carrierId}>
            <select
              className="form-select"
              value={f.carrierId}
              onChange={(e) => set("carrierId", e.target.value)}
            >
              <option value="">Nenhum</option>
              {options.carriers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Modalidade do frete" className="col-md-6">
            <select
              className="form-select"
              value={f.freightMode}
              onChange={(e) => set("freightMode", Number(e.target.value) as FreightMode)}
            >
              {FREIGHT_MODES.map((m) => (
                <option key={m} value={m}>
                  {FREIGHT_MODE_LABELS[m]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Observações" className="col-12" error={errors.notes}>
            <textarea
              rows={3}
              maxLength={5000}
              value={f.notes}
              onChange={(e) => set("notes", e.target.value)}
            />
          </Field>
          <Field label="Observações internas" className="col-12" error={errors.internalNotes}>
            <textarea
              rows={2}
              maxLength={5000}
              value={f.internalNotes}
              onChange={(e) => set("internalNotes", e.target.value)}
            />
          </Field>
        </div>
      </FormSection>
    </FormPage>
  );
}

function ItemsTable({
  f,
  calc,
  errors,
  setItem,
  onRemove,
}: {
  f: PurchaseOrderForm;
  calc: PurchaseOrderCalc | undefined;
  errors: Record<string, string>;
  setItem(key: string, patch: Partial<PurchaseItemRow>): void;
  onRemove(key: string): void;
}) {
  if (f.items.length === 0) return <p className="text-muted">Nenhum item. Busque um produto abaixo.</p>;
  return (
    <div className="table-responsive mb-3">
      <table className="table align-middle" aria-label="Itens do pedido de compra">
        <thead>
          <tr>
            <th scope="col">Descrição</th>
            <th scope="col" style={{ width: "5rem" }}>
              Un
            </th>
            <th scope="col" style={{ width: "7rem" }} className="text-end">
              Qtd
            </th>
            <th scope="col" style={{ width: "10rem" }} className="text-end">
              Preço un.
            </th>
            <th scope="col" style={{ width: "7rem" }} className="text-end">
              IPI %
            </th>
            <th scope="col" className="text-end">
              Total
            </th>
            <th scope="col">
              <span className="visually-hidden">Ações</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {f.items.map((it, i) => {
            const line = calc?.items[i];
            const err = itemError(errors, i);
            const name = it.description || `item ${i + 1}`;
            return (
              <tr key={it.key}>
                <td>
                  {it.description}
                  {it.code && <span className="text-muted small ms-2">{it.code}</span>}
                  {err && <div className="invalid-feedback d-block">{err}</div>}
                </td>
                <td>{it.unit || "—"}</td>
                <td>
                  <QtyInput
                    aria-label={`Quantidade de ${name}`}
                    className="form-control form-control-sm text-end"
                    value={it.qtyQ4}
                    onChange={(v) => setItem(it.key, { qtyQ4: v })}
                  />
                </td>
                <td>
                  <MoneyInput
                    aria-label={`Preço unitário de ${name}`}
                    className="form-control form-control-sm text-end"
                    value={it.unitPriceCents ?? line?.unitPriceCents ?? null}
                    onChange={(v) => setItem(it.key, { unitPriceCents: v })}
                  />
                </td>
                <td>
                  <ScaledInput
                    aria-label={`IPI de ${name}`}
                    className="form-control form-control-sm text-end"
                    value={it.ipiPct4}
                    parse={toPct4}
                    format={pctNumber}
                    onChange={(v) => setItem(it.key, { ipiPct4: v })}
                  />
                </td>
                <td className="text-end text-nowrap">{line ? formatBRL(line.totalCents) : "—"}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    aria-label={`Remover ${name}`}
                    onClick={() => onRemove(it.key)}
                  >
                    Remover
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TotalsBox({
  calc,
  error,
  loading,
}: {
  calc: PurchaseOrderCalc | undefined;
  error: string | null;
  loading: boolean;
}) {
  const rows: [string, number][] = calc
    ? [
        ["Total dos produtos", calc.productsTotalCents],
        ["IPI", calc.ipiTotalCents],
        ["ICMS-ST", calc.icmsStCents],
        ["Desconto", -calc.discountCents],
        ["Frete", calc.freightCents],
        ["Outras despesas", calc.otherExpensesCents],
      ]
    : [];
  return (
    <div className="box card p-3 mb-3" aria-live="polite">
      <h2 className="h6">Totais</h2>
      {error && <div className="alert alert-danger py-2 small">{error}</div>}
      {!calc && !error && <p className="text-muted small mb-0">Escolha o fornecedor e inclua itens.</p>}
      {calc && (
        <dl className="row mb-0 small" aria-busy={loading}>
          {rows.map(([label, v]) => (
            <Fragment key={label}>
              <dt className="col-7 fw-normal">{label}</dt>
              <dd className="col-5 text-end mb-1">{formatBRL(v)}</dd>
            </Fragment>
          ))}
          <dt className="col-7">Total do pedido</dt>
          <dd className="col-5 text-end fw-bold mb-0" data-testid="po-total">
            {formatBRL(calc.totalCents)}
          </dd>
        </dl>
      )}
    </div>
  );
}
